import { Avatar, Box, Divider, Snackbar, Stack, styled, Tooltip } from '@mui/material'
import { Alert, ContentChip, joinDefinedStrings, tokens } from 'ComponentLibrary'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { TextWithEllipsis, Typography } from '..'
import {
    CopyIcon,
    EnvelopeIcon,
    EnvelopeSimpleIcon,
    IconContext,
    MapPinIcon,
    PencilSimpleIcon,
    PenNibIcon,
    PhoneIcon,
    UserCircleDashedIcon,
    UserCircleIcon,
    UserFocusIcon,
} from '@phosphor-icons/react'
import { Address } from 'ComponentLibrary/src/common/address'
import { ExhaustivenessError } from 'ComponentLibrary/src/common/error/error'
import type { UserPresentation } from 'ComponentLibrary/src/common/userPresentations'
import type { UserDisplayData } from 'ComponentLibrary/src/common/users'
import React, { useEffect, useState } from 'react'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { getUserSettingsModalUrl } from 'ComponentLibrary/src/molecules/UserSettings/userSettingsSearchParams'

const getFullName = (salutation?: string, first_name?: string, last_name?: string, email?: string) => {
    return first_name || last_name ? joinDefinedStrings([salutation, first_name, last_name], ' ') : (email ?? '')
}

type SalutationLabels = {
    mr: string
    ms: string
    mrDr: string
    msDr: string
}

export const getLocalizedSalutation = (salutation: string | undefined, labels: SalutationLabels) => {
    if (!salutation || salutation === '-') {
        return undefined
    }

    const normalizedSalutation = salutation.trim()
    const localizedSalutations: Record<string, string> = {
        Herr: labels.mr,
        'Mr.': labels.mr,
        Frau: labels.ms,
        'Ms.': labels.ms,
        'Mrs.': labels.ms,
        'Herr Dr.': labels.mrDr,
        'Mr. Dr.': labels.mrDr,
        'Frau Dr.': labels.msDr,
        'Ms. Dr.': labels.msDr,
        'Mrs. Dr.': labels.msDr,
    }

    return localizedSalutations[normalizedSalutation] ?? normalizedSalutation
}

const getAddress = ({ street, house_number, zip, city, country }: Partial<Address>) => {
    const streetAndNumber = joinDefinedStrings([street, house_number], ' ')
    const zipAndCity = joinDefinedStrings([zip, city], ' ')
    const address = joinDefinedStrings([streetAndNumber, zipAndCity, country], ', ')
    return address !== '' ? address : undefined
}

const useAvatarImageSrc = ({ user, isExternal }: { user?: UserDisplayData; isExternal?: boolean }) => {
    const [avatarImageSrc, setAvatarImageSrc] = useState(user?.profile_thumbnail_image_url)

    useEffect(() => {
        setAvatarImageSrc(user?.profile_thumbnail_image_url)
    }, [user?.profile_thumbnail_image_url])

    const useFallbackImage = () => {
        if (!isExternal && user?.profile_image_url && user.profile_image_url !== avatarImageSrc) {
            setAvatarImageSrc(user.profile_image_url)
        }
    }

    return { avatarImageSrc, useFallbackImage }
}

type ProfileCardProps = {
    user: UserDisplayData
    defaultIcon?: React.ReactNode
    isExternal?: boolean
    copy: UserLinkCopy
}

function ProfileCard({ user, defaultIcon, isExternal, copy }: ProfileCardProps) {
    const {
        role,
        salutation,
        job_title,
        first_name,
        last_name,
        organization_name,
        phone_number,
        certification,
        note,
        city,
        street,
        house_number,
        zip,
        country,
        isCurrentUser,
    } = user
    const email = user.is_active === false ? undefined : user.email
    const name = getFullName(salutation, first_name, last_name, email) || copy.unknown
    const address = getAddress({ street, house_number, zip, city, country })
    const [snackbarMessage, setSnackbarMessage] = useState<string>()
    const navigate = useNavigate()
    const location = useLocation()

    const { avatarImageSrc, useFallbackImage } = useAvatarImageSrc({ user, isExternal })
    const openCurrentUserProfileSettings = () => {
        void navigate(
            getUserSettingsModalUrl({
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
                tab: 'profile',
            })
        )
    }

    return (
        <Stack direction="column" width="300px" padding={tokens.SizingSpacing150} gap={tokens.SizingSpacing200}>
            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing150}>
                <Avatar src={avatarImageSrc} onError={useFallbackImage} sx={{ width: '40px', height: '40px', bgcolor: 'transparent' }}>
                    <IconContext.Provider
                        value={{
                            color: isExternal ? tokens.ColorGraphSequentialThreeDarkest : tokens.ColorIconLinkDefault,
                            size: tokens.SizingIconL,
                            weight: 'regular',
                        }}
                    >
                        {defaultIcon ?? (isExternal ? <UserFocusIcon /> : <UserCircleIcon />)}
                    </IconContext.Provider>
                </Avatar>
                <Stack direction="column">
                    <NameContainer direction="row">
                        <Box maxWidth="200px">
                            <TextWithEllipsis
                                tooltipText={name}
                                typographyProps={{
                                    variant: 'link',
                                    color: tokens.ColorTextLinkDefault,
                                    noWrap: true,
                                }}
                            >
                                {name}
                            </TextWithEllipsis>
                        </Box>
                        {role === 'appraiser' && <PenNibIcon weight="bold" size={tokens.SizingIconS} color={tokens.ColorIconLinkDefault} />}
                    </NameContainer>
                    {certification && (
                        <Box maxWidth="212px">
                            <TextWithEllipsis
                                tooltipText={certification}
                                typographyProps={{
                                    variant: 'textXS',
                                    color: tokens.ColorTextDefault,
                                }}
                            >
                                {certification}
                            </TextWithEllipsis>
                        </Box>
                    )}
                    {job_title && (
                        <Box maxWidth="212px">
                            <TextWithEllipsis
                                tooltipText={job_title}
                                typographyProps={{
                                    variant: 'textXS',
                                    color: tokens.ColorTextDefault,
                                }}
                            >
                                {job_title}
                            </TextWithEllipsis>
                        </Box>
                    )}
                    {organization_name && (
                        <Box maxWidth="200px">
                            <TextWithEllipsis
                                tooltipText={organization_name}
                                typographyProps={{
                                    variant: 'textXS',
                                    color: tokens.ColorTextDefault,
                                }}
                            >
                                {organization_name}
                            </TextWithEllipsis>
                        </Box>
                    )}
                </Stack>
            </Stack>
            {isExternal && (
                <ExternalParticipantInfoContainer>
                    <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                        <EnvelopeIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorGraphSequentialThreeDarkest} />
                        <Typography variant="textS" color={tokens.ColorGraphSequentialThreeDarkest}>
                            {copy.external}
                        </Typography>
                    </Stack>
                    <Typography variant="textXS" color={tokens.ColorTextDefault}>
                        {copy.externalEmailInfo}
                    </Typography>
                </ExternalParticipantInfoContainer>
            )}
            {(email || phone_number || address) && (
                <>
                    <Divider sx={{ borderColor: tokens.ColorBorderDefault }} />
                    <Stack direction="column" gap={tokens.SizingSpacing075}>
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing075}>
                            <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                                {copy.contact}
                            </Typography>
                            {isCurrentUser && (
                                <IconButton
                                    variant="tertiary"
                                    tooltip={copy.editOwnContactTooltip}
                                    size="small"
                                    onClick={openCurrentUserProfileSettings}
                                    icon={<PencilSimpleIcon />}
                                />
                            )}
                        </Stack>
                        {email && (
                            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                                <EnvelopeSimpleIcon
                                    size={tokens.SizingIconS}
                                    weight="bold"
                                    color={tokens.ColorIconDefault}
                                    style={{ flexShrink: 0 }}
                                />
                                <Box maxWidth="212px">
                                    <TextWithEllipsis
                                        tooltipText={email}
                                        typographyProps={{ variant: 'textS', color: tokens.ColorTextDefault }}
                                    >
                                        {email}
                                    </TextWithEllipsis>
                                </Box>
                                <IconButton
                                    size="small"
                                    variant="tertiary"
                                    icon={<CopyIcon />}
                                    onClick={() => {
                                        void navigator.clipboard.writeText(email)
                                        setSnackbarMessage(copy.emailCopied)
                                    }}
                                />
                            </Stack>
                        )}
                        {phone_number && (
                            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                                <PhoneIcon
                                    size={tokens.SizingIconS}
                                    weight="bold"
                                    color={tokens.ColorIconDefault}
                                    style={{ flexShrink: 0 }}
                                />
                                <Typography variant="textS" color={tokens.ColorTextDefault}>
                                    {phone_number}
                                </Typography>
                                <IconButton
                                    size="small"
                                    variant="tertiary"
                                    icon={<CopyIcon />}
                                    onClick={() => {
                                        void navigator.clipboard.writeText(phone_number)
                                        setSnackbarMessage(copy.phoneCopied)
                                    }}
                                />
                            </Stack>
                        )}
                        {address && (
                            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                                <MapPinIcon
                                    size={tokens.SizingIconS}
                                    weight="bold"
                                    color={tokens.ColorIconDefault}
                                    style={{ flexShrink: 0 }}
                                />
                                <Box maxWidth="212px">
                                    <TextWithEllipsis
                                        tooltipText={address}
                                        typographyProps={{ variant: 'textS', color: tokens.ColorTextDefault }}
                                    >
                                        {address}
                                    </TextWithEllipsis>
                                </Box>
                                <IconButton
                                    size="small"
                                    variant="tertiary"
                                    icon={<CopyIcon />}
                                    onClick={() => {
                                        void navigator.clipboard.writeText(address)
                                        setSnackbarMessage(copy.addressCopied)
                                    }}
                                />
                            </Stack>
                        )}
                    </Stack>
                </>
            )}
            {note && (
                <>
                    <Divider sx={{ borderColor: tokens.ColorBorderDefault }} />
                    <Stack direction="column" gap={tokens.SizingSpacing075}>
                        <Typography variant="headline4" color={tokens.ColorIconBoldest}>
                            {copy.note}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {note}
                        </Typography>
                    </Stack>
                </>
            )}
            <Snackbar
                open={snackbarMessage !== undefined}
                onClose={() => setSnackbarMessage(undefined)}
                autoHideDuration={2000}
                sx={{ width: '100%' }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <div>
                    <Alert severity="success" size="small">
                        {snackbarMessage}
                    </Alert>
                </div>
            </Snackbar>
        </Stack>
    )
}

type UserLinkProps = {
    size?: 'small' | 'medium' | 'large'
    href: string
    forceReload?: boolean
    inactive?: boolean
    user?: UserDisplayData
    showProfileOnHover?: boolean
    disableEllipsisHover?: boolean
    maxWidth?: string
    defaultIcon?: React.ReactNode
    showAddressInsteadOfOrganization?: boolean
    disablePortal?: boolean
    isExternal?: boolean
    isAccount?: boolean
    showNonUserChip?: boolean
    copy?: Partial<UserLinkCopy>
}

export type UserLinkCopy = {
    me: string
    external: string
    unknown: string
    unavailable: string
    contact: string
    editOwnContactTooltip: string
    emailCopied: string
    phoneCopied: string
    addressCopied: string
    note: string
    externalEmailInfo: string
}

export const getDefaultUserLinkCopy = (tCommon: TFunction): UserLinkCopy => ({
    me: tCommon('userLink.me', { defaultValue: 'Ich' }),
    external: tCommon('userLink.external', { defaultValue: 'Extern' }),
    unknown: tCommon('userLink.unknown', { defaultValue: 'Nicht festgelegt' }),
    unavailable: tCommon('userLink.unavailable', { defaultValue: 'Benutzer nicht verfügbar' }),
    contact: tCommon('userLink.contact', { defaultValue: 'Kontakt' }),
    editOwnContactTooltip: tCommon('userLink.editOwnContactTooltip', { defaultValue: 'Eigene Kontaktdaten bearbeiten' }),
    emailCopied: tCommon('userLink.emailCopied', { defaultValue: 'Email-Adresse kopiert' }),
    phoneCopied: tCommon('userLink.phoneCopied', { defaultValue: 'Telefonnummer kopiert' }),
    addressCopied: tCommon('userLink.addressCopied', { defaultValue: 'Adresse kopiert' }),
    note: tCommon('userLink.note', { defaultValue: 'Anmerkung' }),
    externalEmailInfo: tCommon('userLink.externalEmailInfo', { defaultValue: 'Dieser Kontakt kollaboriert über E-Mail.' }),
})

export const DEFAULT_USER_LINK_COPY: UserLinkCopy = {
    me: 'Ich',
    external: 'Extern',
    unknown: 'Nicht festgelegt',
    unavailable: 'Benutzer nicht verfügbar',
    contact: 'Kontakt',
    editOwnContactTooltip: 'Eigene Kontaktdaten bearbeiten',
    emailCopied: 'Email-Adresse kopiert',
    phoneCopied: 'Telefonnummer kopiert',
    addressCopied: 'Adresse kopiert',
    note: 'Anmerkung',
    externalEmailInfo: 'Dieser Kontakt kollaboriert über E-Mail.',
}

function BaseUserLink({
    size = 'medium',
    inactive,
    user,
    showProfileOnHover,
    disableEllipsisHover,
    maxWidth = '200px',
    defaultIcon,
    showAddressInsteadOfOrganization,
    disablePortal,
    isExternal,
    isAccount,
    showNonUserChip,
    copy,
}: Omit<UserLinkProps, 'href' | 'forceReload'>) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const resolvedCopy = { ...getDefaultUserLinkCopy(tCommon), ...copy }
    const salutationLabels = {
        mr: tCommon('salutations.mr', { defaultValue: 'Herr' }),
        ms: tCommon('salutations.ms', { defaultValue: 'Frau' }),
        mrDr: tCommon('salutations.mrDr', { defaultValue: 'Herr Dr.' }),
        msDr: tCommon('salutations.msDr', { defaultValue: 'Frau Dr.' }),
    }
    const localizedUser = user
        ? {
              ...user,
              email: user.is_active === false ? undefined : user.email,
              salutation: getLocalizedSalutation(user.salutation, salutationLabels),
          }
        : user
    const isUnknown = !localizedUser?.first_name && !localizedUser?.last_name && !localizedUser?.email && !localizedUser?.organization_name
    const name =
        getFullName(
            size === 'large' ? localizedUser?.salutation : undefined,
            localizedUser?.first_name,
            localizedUser?.last_name,
            localizedUser?.email
        ) || resolvedCopy.unknown
    const isUserWithAccount = isAccount ?? (user?.id !== undefined || user?.uuid !== undefined)
    const isLinkedUser = isUserWithAccount || !!isExternal
    const address = getAddress({ ...user })
    const secondaryText = showAddressInsteadOfOrganization ? address : user?.organization_name
    const secondaryTextColor = inactive ? tokens.ColorTextDisabled : isLinkedUser ? tokens.ColorTextDefault : tokens.ColorTextSubtle

    const hasInfoToShowOnHover = !!address || !!localizedUser?.email || !!user?.note || !!user?.job_title || !!user?.phone_number
    const isHoverActive = showProfileOnHover && !isUnknown && hasInfoToShowOnHover

    const { avatarImageSrc, useFallbackImage } = useAvatarImageSrc({ user, isExternal })

    return (
        <Tooltip
            title={
                isHoverActive ? (
                    <ProfileCard user={{ ...localizedUser }} defaultIcon={defaultIcon} isExternal={isExternal} copy={resolvedCopy} />
                ) : undefined
            }
            placement="right"
            enterDelay={500}
            PopperProps={{ disablePortal, onClick: (e) => e.stopPropagation() }}
            slotProps={{
                tooltip: {
                    sx: {
                        bgcolor: tokens.ColorSurfaceDefault,
                        borderRadius: tokens.SizingCornerS,
                        padding: 0,
                        ...tokens.Popup,
                    },
                },
            }}
        >
            <Stack
                direction="row"
                gap={size === 'small' ? tokens.SizingSpacing075 : size === 'medium' ? tokens.SizingSpacing100 : tokens.SizingSpacing150}
                alignItems="center"
                minWidth={0}
                maxWidth="100%"
                height={size === 'small' ? tokens.FontLineheightSm : undefined}
                sx={{ cursor: 'default' }}
            >
                <StyledAvatar
                    src={avatarImageSrc}
                    onError={useFallbackImage}
                    $size={size}
                    $inactive={inactive || isUnknown}
                    $isExternal={isExternal}
                >
                    <IconContext.Provider
                        value={{
                            color:
                                inactive || isUnknown
                                    ? tokens.ColorTextDisabled
                                    : isExternal
                                      ? tokens.ColorGraphSequentialThreeDarkest
                                      : tokens.ColorIconBoldest,
                            size: '75%',
                            weight: size === 'small' ? 'bold' : undefined,
                        }}
                    >
                        {defaultIcon ?? (isExternal ? <UserFocusIcon /> : isUnknown ? <UserCircleDashedIcon /> : <UserCircleIcon />)}
                    </IconContext.Provider>
                </StyledAvatar>
                <Stack
                    direction="row"
                    gap={size === 'small' ? tokens.SizingSpacing075 : size === 'medium' ? tokens.SizingSpacing100 : tokens.SizingSpacing150}
                    alignItems="center"
                    minWidth={0}
                    maxWidth={maxWidth}
                >
                    <Stack direction="column" style={{ flexGrow: 1, minWidth: 0 }}>
                        <NameContainer direction="row">
                            <Box maxWidth="100%" style={{ minWidth: 0 }}>
                                <TextWithEllipsis
                                    tooltipText={isHoverActive || disableEllipsisHover ? '' : name}
                                    typographyProps={{
                                        variant: isLinkedUser ? 'link' : 'textS',
                                        color:
                                            inactive || isUnknown
                                                ? tokens.ColorTextDisabled
                                                : isLinkedUser
                                                  ? tokens.ColorTextLinkDefault
                                                  : tokens.ColorTextDefault,
                                        noWrap: true,
                                    }}
                                >
                                    {name}
                                </TextWithEllipsis>
                            </Box>
                            {user?.role === 'appraiser' && (
                                <PenNibIcon
                                    weight="bold"
                                    style={{ flexShrink: 0 }}
                                    size={tokens.SizingIconS}
                                    color={
                                        inactive || isUnknown
                                            ? tokens.ColorTextDisabled
                                            : isLinkedUser
                                              ? tokens.ColorTextLinkDefault
                                              : tokens.ColorIconDefault
                                    }
                                />
                            )}
                        </NameContainer>
                        {size === 'large' && !isExternal && user?.role === 'appraiser' && user.certification && (
                            <Box sx={{ maxWidth: '100%' }}>
                                <TextWithEllipsis
                                    tooltipText={isHoverActive ? '' : user.certification}
                                    typographyProps={{ variant: 'textXS', color: secondaryTextColor }}
                                >
                                    {user.certification}
                                </TextWithEllipsis>
                            </Box>
                        )}
                        {size === 'large' && !isExternal && user?.job_title && (
                            <Box sx={{ maxWidth: '100%' }}>
                                <TextWithEllipsis
                                    tooltipText={isHoverActive ? '' : user.job_title}
                                    typographyProps={{ variant: 'textXS', color: secondaryTextColor }}
                                >
                                    {user.job_title}
                                </TextWithEllipsis>
                            </Box>
                        )}
                        {secondaryText && size !== 'small' && (
                            <Box sx={{ maxWidth: '100%' }}>
                                <TextWithEllipsis
                                    tooltipText={isHoverActive ? '' : secondaryText}
                                    typographyProps={{ variant: 'textXS', color: secondaryTextColor }}
                                >
                                    {secondaryText}
                                </TextWithEllipsis>
                            </Box>
                        )}
                    </Stack>
                    {user?.isCurrentUser && (
                        <Box flexShrink={0}>
                            <ContentChip size="small" color="green" label={resolvedCopy.me} />
                        </Box>
                    )}
                    {showNonUserChip && (
                        <Box flexShrink={0}>
                            <ContentChip size="small" color="purple" label={resolvedCopy.external} />
                        </Box>
                    )}
                    {isExternal && (
                        <Box flexShrink={0}>
                            <ContentChip size="small" color="turquoise" label={resolvedCopy.external} icon={<EnvelopeIcon />} />
                        </Box>
                    )}
                </Stack>
            </Stack>
        </Tooltip>
    )
}

export function UserLink({ size = 'medium', href, forceReload, inactive: _inactive, ...userLinkProps }: UserLinkProps) {
    return (
        <StyledUserLink to={href} reloadDocument={forceReload} $size={size}>
            <BaseUserLink size={size} {...userLinkProps} />
        </StyledUserLink>
    )
}

// For cases where the linked page is not built yet (like profile pages). We probably need to check all usages before we actually link since this is used in dropdowns.
export function DisplayUserLink({
    size = 'medium',
    showProfileOnHover = true,
    ...userLinkProps
}: Omit<UserLinkProps, 'href' | 'forceReload'>) {
    return (
        <StyledDisplayUserLink $size={size}>
            <BaseUserLink size={size} showProfileOnHover={showProfileOnHover} {...userLinkProps} />
        </StyledDisplayUserLink>
    )
}

type DisplayUserPresentationProps = {
    presentation: UserPresentation
    size?: 'small' | 'medium' | 'large'
    maxWidth?: string
    copy?: Partial<UserLinkCopy>
    showProfileOnHover?: boolean
    disableEllipsisHover?: boolean
    showNonUserChip?: boolean
}

export function DisplayUserPresentation({
    presentation,
    size,
    maxWidth,
    copy,
    showProfileOnHover,
    disableEllipsisHover,
    showNonUserChip,
}: DisplayUserPresentationProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const sharedProps = { size, maxWidth, disableEllipsisHover, copy }

    switch (presentation.kind) {
        case 'account':
            return <DisplayUserLink {...sharedProps} user={presentation.displayData} isAccount showProfileOnHover={showProfileOnHover} />
        case 'nonUser': {
            const { role: _role, ...displayDataWithoutRole } = presentation.displayData
            return (
                <DisplayUserLink
                    {...sharedProps}
                    user={displayDataWithoutRole}
                    showNonUserChip={showNonUserChip}
                    showProfileOnHover={false}
                />
            )
        }
        case 'externalParticipant':
            return <DisplayUserLink {...sharedProps} user={presentation.displayData} isExternal showProfileOnHover={showProfileOnHover} />
        case 'unavailable': {
            const resolvedCopy = { ...getDefaultUserLinkCopy(tCommon), ...copy }
            return (
                <DisplayUserLink
                    {...sharedProps}
                    user={{ first_name: resolvedCopy.unavailable }}
                    inactive
                    showProfileOnHover={false}
                    defaultIcon={<UserCircleDashedIcon aria-label={resolvedCopy.unavailable} />}
                />
            )
        }
        default:
            return ExhaustivenessError(presentation)
    }
}

const UserLinkStyles = ({ $size }: { $size?: UserLinkProps['size'] }) => ({
    display: 'inline-flex',
    gap: $size === 'small' ? tokens.SizingSpacing050 : tokens.SizingSpacing100,
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '100%',
})

const StyledUserLink = styled(RouterLink, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $size?: UserLinkProps['size'] }>(({ $size }) => ({
    ...UserLinkStyles({ $size }),
    cursor: 'pointer',
    textDecoration: 'none',
}))

const StyledDisplayUserLink = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $size?: UserLinkProps['size'] }>(({ $size }) => UserLinkStyles({ $size }))

const StyledAvatar = styled(Avatar, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $size?: 'small' | 'medium' | 'large'; $inactive?: boolean; $isExternal?: boolean }>(({ $size, $inactive, $isExternal }) => ({
    width: $size === 'small' ? tokens.SizingIconS : $size === 'medium' ? tokens.SizingIconM : tokens.SizingIconL,
    height: $size === 'small' ? tokens.SizingIconS : $size === 'medium' ? tokens.SizingIconM : tokens.SizingIconL,
    backgroundColor: $isExternal ? tokens.ColorGraphSequentialThreeLightest : tokens.ColorSurfaceAccentRoyalDefault,
    opacity: $inactive ? '0.5' : undefined,
}))

const ExternalParticipantInfoContainer = styled(Stack)(() => ({
    gap: tokens.SizingSpacing050,
    backgroundColor: tokens.ColorGraphSequentialThreeLightest,
    borderRadius: tokens.SizingCornerS,
    padding: tokens.SizingSpacing075,
}))

const NameContainer = styled(Stack)(() => ({
    gap: tokens.SizingSpacing050,
    alignItems: 'center',
    'a:hover > div > & > p': {
        color: tokens.ColorTextLinkHover,
    },
    'a:hover > div > & > svg': {
        fill: tokens.ColorIconLinkHover,
    },
}))
